// ─────────────────────────────────────────────
//  ProfilePage — the signed-in user edits their own information.
//  Reached from the topbar avatar circle (every page) at /profile.
//  Profile fields write to users/{uid}; account changes (email, password) go
//  through Firebase Auth re-authentication helpers in firebase/auth.js.
// ─────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateUser, mirrorContactToStaff } from "../firebase/firestore";
import { changePassword, changeEmail, resetPassword } from "../firebase/auth";
import { contactPhone, personContactPatch } from "../domain/contact";
import { Card, Field, Button, Avatar, SignOutButton } from "../components";
import tokens from "../theme/tokens";

const { color, font } = tokens;

// Map raw Firebase Auth error codes to messages a clinician can act on.
function friendlyAuthError(err) {
  const code = err?.code || "";
  if (code === "auth/wrong-password" || code === "auth/invalid-credential")
    return "Current password is incorrect.";
  if (code === "auth/weak-password") return "New password must be at least 6 characters.";
  if (code === "auth/email-already-in-use") return "That email is already in use.";
  if (code === "auth/invalid-email") return "That email address is not valid.";
  if (code === "auth/requires-recent-login") return "Please sign out and back in, then try again.";
  if (code === "auth/operation-not-allowed")
    return "Email changes are disabled for this project. Contact your administrator.";
  return err?.message || "Something went wrong. Please try again.";
}

export default function ProfilePage() {
  const { user, isDoctor } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // Profile form — seeded from the current profile.
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  // ⚠️ ONE PHONE FIELD, NOT TWO. The v2 contract names this `phoneNumber`;
  // `users.phone` already exists and is already live (buildDefaultProfile
  // seeds it, scripts/syncStaffAddresses.mjs reads it, the board dials it), so
  // `phone` stays CANONICAL and `phoneNumber` is a read-only alias resolved by
  // domain/contact.contactPhone. Nothing here writes `phoneNumber`. Two phone
  // fields on one person is how a directory starts showing a stale number.
  const [phone, setPhone] = useState(contactPhone(user));
  const [address, setAddress] = useState(user?.address || "");
  const [shortCode, setShortCode] = useState(user?.shortCode || "");
  const [savingProfile, setSavingProfile] = useState(false);

  // Account forms.
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [emailPw, setEmailPw] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  // Phase 6 (native only): biometric app-lock opt-in. On web bioAvailable
  // stays false and the whole App Security card is not rendered.
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioEnabled, setBioEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { isNative } = await import("../native/platform");
      if (!isNative()) return;
      const [{ biometricsAvailable }, { getAppLockEnabled }] = await Promise.all([
        import("../native/biometrics"),
        import("../native/appLock"),
      ]);
      const avail = await biometricsAvailable();
      const enabled = avail && (await getAppLockEnabled());
      if (cancelled) return;
      setBioAvailable(avail);
      setBioEnabled(enabled);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleToggleBiometric = async (e) => {
    const next = e.target.checked;
    const [{ biometricAuthenticate }, { setAppLockEnabled }] = await Promise.all([
      import("../native/biometrics"),
      import("../native/appLock"),
    ]);
    if (next) {
      // Prove the sensor works before locking the app behind it.
      const ok = await biometricAuthenticate("Confirm biometric unlock for ATC");
      if (!ok) { toast.error("Biometric check didn't complete — lock not enabled."); return; }
    }
    await setAppLockEnabled(next);
    setBioEnabled(next);
    toast.success(next ? "Biometric unlock required on launch." : "Biometric lock disabled.");
  };

  // Mirror the person's OWN contact details onto their linked `staff` record
  // (matched server-side on staff.uid), because the Team directory reads
  // `staff`, not `users`. Without it the directory keeps showing whatever an
  // administrator typed months ago while the person is certain they changed it.
  //
  // ⚠️ A FAILED MIRROR MUST NOT FAIL THE SAVE. The profile write already
  // succeeded by the time this runs, and the common failure is benign: this
  // login has no linked staff record yet (mirrorContactToStaff returns 0), or
  // the person is a role that owns no roster entry. Reporting "could not save
  // your profile" after the profile was saved is a worse lie than a directory
  // that lags. Surfaced as a warning toast so it is not silent either.
  const mirrorContact = async ({ phone: p, email: e }) => {
    try {
      await mirrorContactToStaff(user.uid, personContactPatch({ phone: p, email: e }));
    } catch (err) {
      console.error("Contact mirror to staff failed:", err);
      toast.error("Saved — but the Team directory could not be updated. Tell an admin.");
    }
  };

  const handleSaveProfile = async () => {
    if (!firstName.trim() && !lastName.trim()) {
      toast.error("Enter at least a first or last name.");
      return;
    }
    setSavingProfile(true);
    try {
      const name = `${firstName.trim()} ${lastName.trim()}`.trim();
      await updateUser(user.uid, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        name,
        phone: phone.trim(),
        address: address.trim(),
        shortCode: shortCode.trim().toUpperCase(),
      });
      await mirrorContact({ phone: phone.trim(), email: user.email });
      toast.success("Profile saved. Reload to update your avatar everywhere.");
    } catch (err) {
      console.error("Save profile failed:", err);
      toast.error("Could not save your profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim() || !emailPw) {
      toast.error("Enter the new email and your current password.");
      return;
    }
    setSavingEmail(true);
    try {
      await changeEmail(emailPw, newEmail.trim());
      setEmailPw("");
      // Same field, same directory. changeEmail() already writes users.email;
      // the staff record has to follow or the directory offers a mailto: to an
      // address this person no longer reads.
      await mirrorContact({ phone: phone.trim(), email: newEmail.trim() });
      toast.success("Email updated.");
    } catch (err) {
      console.error("Change email failed:", err);
      toast.error(friendlyAuthError(err));
    } finally {
      setSavingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!curPw || !newPw) {
      toast.error("Enter your current and new password.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSavingPw(true);
    try {
      await changePassword(curPw, newPw);
      setCurPw(""); setNewPw(""); setConfirmPw("");
      toast.success("Password changed.");
    } catch (err) {
      console.error("Change password failed:", err);
      toast.error(friendlyAuthError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const handleResetEmail = async () => {
    try {
      await resetPassword(user.email);
      toast.success(`Password reset link sent to ${user.email}.`);
    } catch (err) {
      console.error("Reset email failed:", err);
      toast.error("Could not send the reset email.");
    }
  };

  if (!user) return null;

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <button style={s.back} onClick={() => navigate(-1)} aria-label="Go back">← Back</button>
        <span style={s.title}>My Profile</span>
        {/* Grouped, not a fourth child: this topbar is space-between, so a bare
            sibling would push the title off centre. */}
        <div style={s.topbarRight}>
          <Avatar user={{ firstName, lastName, name: user.name, shortCode }} isDoctor={isDoctor} />
          <SignOutButton />
        </div>
      </div>

      <div style={s.body}>
        {/* PROFILE INFO */}
        <Card style={s.card}>
          <h2 style={s.h2}>Personal Information</h2>
          <div style={s.grid2}>
            <Field id="firstName" label="First name" value={firstName}
              onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" />
            <Field id="lastName" label="Last name" value={lastName}
              onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
          </div>
          <Field id="phone" label="Phone number" type="tel" value={phone}
            onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567"
            helperText="Shown to your colleagues in the Team directory." />
          {/* Not an input: the email in the directory IS the sign-in email,
              changed in the card below with your password. Showing it here —
              rather than adding a second, separately-editable "contact email" —
              is the same one-canonical-field rule the phone follows. */}
          <p style={s.note}>
            Team directory email: <strong>{user.email || "—"}</strong> — change it
            under “Email Address” below.
          </p>
          <Field id="address" label="Address" value={address}
            onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, City, ST" />
          <Field id="shortCode" label="Short code" value={shortCode}
            onChange={(e) => setShortCode(e.target.value.toUpperCase())}
            placeholder="e.g. TAN" maxLength={5}
            helperText={isDoctor ? "Shown on your avatar and the assignment grid." : "Optional."} />
          <div style={s.actions}>
            <Button variant="primary" onClick={handleSaveProfile} loading={savingProfile}>
              Save profile
            </Button>
          </div>
        </Card>

        {/* ACCOUNT — EMAIL */}
        <Card style={s.card}>
          <h2 style={s.h2}>Email Address</h2>
          <p style={s.note}>Your sign-in email. Changing it requires your current password.</p>
          <Field id="newEmail" label="Email" type="email" value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)} placeholder="you@hospital.com" />
          <Field id="emailPw" label="Current password" type="password" value={emailPw}
            onChange={(e) => setEmailPw(e.target.value)} placeholder="••••••••" />
          <div style={s.actions}>
            <Button variant="secondary" onClick={handleChangeEmail} loading={savingEmail}>
              Update email
            </Button>
          </div>
        </Card>

        {/* ACCOUNT — PASSWORD */}
        <Card style={s.card}>
          <h2 style={s.h2}>Password</h2>
          <Field id="curPw" label="Current password" type="password" value={curPw}
            onChange={(e) => setCurPw(e.target.value)} placeholder="••••••••" />
          <div style={s.grid2}>
            <Field id="newPw" label="New password" type="password" value={newPw}
              onChange={(e) => setNewPw(e.target.value)} placeholder="At least 6 characters" />
            <Field id="confirmPw" label="Confirm new password" type="password" value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)} placeholder="Repeat new password" />
          </div>
          <div style={s.actions}>
            <Button variant="primary" onClick={handleChangePassword} loading={savingPw}>
              Change password
            </Button>
            <Button variant="secondary" onClick={handleResetEmail}>
              Email me a reset link
            </Button>
          </div>
        </Card>

        {/* APP SECURITY — native app only (Phase 6) */}
        {bioAvailable && (
          <Card style={s.card}>
            <h2 style={s.h2}>App Security</h2>
            <p style={s.note}>
              Require your fingerprint or face to open the app on this device.
              Your account password is never stored on the device.
            </p>
            <label htmlFor="bioLock" style={s.toggleRow}>
              <input
                id="bioLock"
                type="checkbox"
                checked={bioEnabled}
                onChange={handleToggleBiometric}
                style={s.toggleBox}
              />
              Require biometric unlock
            </label>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── Styles (light token theme) ────────────────
const s = {
  page: { background: color.surface.base, color: color.text.primary, minHeight: "100vh", fontFamily: font.family },
  topbar: {
    background: color.surface.card, borderBottom: `1px solid ${color.border.default}`,
    padding: "0 20px", height: 56, display: "flex", alignItems: "center",
    justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10,
  },
  topbarRight: { display: "flex", alignItems: "center", gap: 10 },
  back: { background: "transparent", border: "none", color: color.accent.primary, cursor: "pointer", fontSize: font.size.body, fontFamily: "inherit" },
  title: { fontSize: font.size.heading, fontWeight: font.weight.bold },
  body: { maxWidth: 640, margin: "0 auto", padding: "22px 18px", display: "flex", flexDirection: "column", gap: 16 },
  card: { padding: 20 },
  h2: { margin: "0 0 14px", fontSize: font.size.body, fontWeight: font.weight.bold, color: color.text.primary },
  note: { margin: "0 0 12px", fontSize: font.size.label, color: color.text.secondary },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  actions: { display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" },
  toggleRow: { display: "flex", alignItems: "center", gap: 10, fontSize: font.size.body, cursor: "pointer", minHeight: 44 },
  toggleBox: { width: 20, height: 20, accentColor: color.accent.primary, cursor: "pointer" },
};
